/*
 * A basket cannot hold minus two of anything.
 *
 * The theme's own quantity handler checks the maximum and never the minimum:
 *
 *   } else if (change.decrease) {
 *     newQuantity -= quantityInput.step || 1;   // no floor
 *
 * The input carries min="0" and the handler does not read it, so pressing the
 * minus on a single item takes the line to zero, then to minus one, and the
 * page cheerfully shows it. Shopify treats zero as a removal, so what the
 * customer sees and what the cart is about to do have already parted company.
 *
 * That handler lives in the theme's compiled main.js, which is vendor code and
 * is replaced wholesale by a theme update — a fix edited into it would be
 * silently undone by somebody pressing Update in the admin. So it is guarded
 * from outside instead: this listens in the capture phase, which runs before
 * the theme's delegated listener, and stops the event when the press would
 * take a line below one.
 *
 * Below one it removes the line, which is what the press means. A customer
 * pressing minus on their last one is saying they do not want it.
 */
(function () {
  function guard(event) {
    var button = event.target.closest && event.target.closest('.quantity-down');
    if (!button) return;

    var row = button.closest('.cart-item') || button.closest('[data-cart-item]');
    if (!row) return;

    var input = row.querySelector('.cart-item__quantity-input, input[name="updates[]"]');
    if (!input) return;

    var current = parseInt(input.value, 10);
    if (!Number.isFinite(current) || current > 1) return;   // let the theme have it
    if (!ajaxCart(row)) return;                             // and where nothing would redraw

    /* The theme's own handler must not run: it would subtract anyway. */
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    removeLine(row, input);
  }

  /*
   * Remove one line, asking about one line.
   *
   * The theme removes by rewriting the whole basket: it sets that row's box to
   * zero and then posts every quantity box on the page in a single
   * /cart/update.js, keyed by line. Shopify validates that map as a unit, so
   * one bad entry in it fails all of it — 422, "Cannot find variant", nothing
   * applied. Confirmed against the live shop: the same removal succeeds alone
   * and is refused when one unrelated bad key rides along.
   *
   * What the customer sees when that happens is the item leaving and coming
   * straight back, because the row is hidden optimistically, the request fails,
   * and the next refresh redraws the basket from a server that still has it.
   * They never see an error: the theme's catch is a console.log.
   *
   * So a removal is sent on its own, through /cart/change.js, where the only
   * line that can invalidate the request is the one being removed. Whatever is
   * wrong with some other row stops being able to trap a customer with a
   * basket they cannot empty.
   *
   * Increases and decreases are left to the theme. They have the same flaw and
   * it is worth fixing, but not by reimplementing its debounce, its maximum and
   * its popup out here — and being unable to remove something is the failure
   * that costs an order.
   */
  /*
   * Only where something is listening to redraw.
   *
   * Taking the removal over means the page will not reload, so the basket on
   * screen has to be redrawn by whatever hears on:cart:change. In the drawer
   * that is <cart-form data-ajax-update>. Somewhere without one, intercepting
   * would leave the customer pressing remove and watching nothing happen — the
   * link's own href, a real /cart/change, is the better answer there.
   */
  function ajaxCart(row) {
    return !!(row.closest && row.closest('[data-ajax-update]'));
  }

  function removeLine(row, input) {
    var key = input.getAttribute('data-key');
    var link = row.querySelector('.cart-item__remove');

    /* One removal per row. A second press while the first is in flight would
       ask to remove a line that is already going, and the answer to that is an
       error — which below means a page load, for a row the customer has
       already successfully removed. Impatience should not cost a reload. */
    if (row.getAttribute('data-tc-removing')) return;
    row.setAttribute('data-tc-removing', '1');

    if (!key) {
      /* Nothing to address the line by. The theme's own path is worse than
         useless here, but the link is a real href to /cart/change and a page
         load still removes it. */
      if (link) { window.location.href = link.getAttribute('href'); }
      return;
    }

    row.classList.add('merge-item-refreshing');
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart/change ' + r.status);
        /* Told the way the theme tells it, so the drawer redraws and the
           header's basket count follows. */
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      })
      .catch(function () {
        /* Fall back to the link's own href rather than leaving the row in a
           half-removed state: a page load is slower and always works. */
        row.removeAttribute('data-tc-removing');
        row.classList.remove('merge-item-refreshing');
        if (link) window.location.href = link.getAttribute('href');
      });
  }

  document.addEventListener('click', guard, true);

  /*
   * The remove link itself, for the same reason.
   *
   * It is the other way to say "take this out", and it goes through the same
   * whole-basket rewrite, so it fails in the same way and puts the item back
   * just as visibly.
   */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('.cart-item__remove');
    if (!link) return;
    var row = link.closest('.cart-item') || link.closest('[data-cart-item]');
    if (!row) return;
    var input = row.querySelector('.cart-item__quantity-input, input[name="updates[]"]');
    if (!input || !input.getAttribute('data-key')) return;  // let the href do it
    if (!ajaxCart(row)) return;                             // ditto where nothing would redraw

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    removeLine(row, input);
  }, true);

  /*
   * And the box itself, which can be typed into. The same handler passes
   * whatever is in it straight to the cart, so "-3" is sent as "-3".
   */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('.cart-item__quantity-input, input[name="updates[]"]')) return;
    var value = parseInt(input.value, 10);
    if (Number.isFinite(value) && value < 0) input.value = 0;
  }, true);
})();
